import { useState, useEffect } from "react";
import { useAuth } from "../contex/AuthContext";
import {
  subscribeToTasks,
  subscribeToNotes,
  subscribeToCourses,
  subscribeToExpenses,
  subscribeToProfile,
} from "../lib/db";

export function useTasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const unsub = subscribeToTasks((data) => {
      setTasks(data);
      setLoading(false);
    });
    return unsub;
  }, [user]);

  const pending = tasks.filter(t => !t.done);
  const done = tasks.filter(t => t.done);

  return { tasks, pending, done, loading };
}

export function useNotes() {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const unsub = subscribeToNotes((data) => {
      setNotes(data);
      setLoading(false);
    });
    return unsub;
  }, [user]);

  return { notes, loading };
}

export function useCourses() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const unsub = subscribeToCourses((data) => {
      setCourses(data);
      setLoading(false);
    });
    return unsub;
  }, [user]);

  const getCourse = (id) => courses.find(c => c.id === id);

  return { courses, getCourse, loading };
}

export function useExpenses() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const unsub = subscribeToExpenses((data) => {
      setExpenses(data);
      setLoading(false);
    });
    return unsub;
  }, [user]);

  const totalSpent = expenses.reduce((sum, e) => sum + e.amount, 0);
  const spentByCategory = expenses.reduce((acc, e) => {
    acc[e.category] = (acc[e.category] || 0) + e.amount;
    return acc;
  }, {});

  return { expenses, totalSpent, spentByCategory, loading };
}

export function useProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!user) return;
    const unsub = subscribeToProfile((data) => setProfile(data));
    return unsub;
  }, [user]);

  return { profile };
}

export function useTimer() {
  const [running, setRunning] = useState(false);
  const [remaining, setRemaining] = useState(25 * 60);
  const [total, setTotal] = useState(25 * 60);
  const [mode, setMode] = useState("pomodoro");

  useEffect(() => {
    if (!running) return;
    const interval = setInterval(() => {
      setRemaining(r => {
        if (r <= 1) {
          setRunning(false);
          clearInterval(interval);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [running]);

  const switchMode = (mins, modeName) => {
    setRunning(false);
    setTotal(mins * 60);
    setRemaining(mins * 60);
    setMode(modeName);
  };

  const toggle = () => setRunning(r => !r);
  const reset = () => { setRunning(false); setRemaining(total); };
  const fraction = remaining / total;
  const isComplete = remaining === 0;

  const display = () => {
    const m = Math.floor(remaining / 60);
    const s = remaining % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  return { running, remaining, total, mode, fraction, isComplete, display, toggle, reset, switchMode };
}